import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Button, 
  useTheme,
  CircularProgress,
  Alert
} from '@mui/material';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';
import PageSalesCta from '../components/PageSalesCta';

const Portfolio = () => {
  const theme = useTheme();
  const [projects, setProjects] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('Fetching portfolio config and projects from API...');
      const [configResponse, projectsResponse] = await Promise.all([
        apiService.getPortfolioConfig(),
        apiService.getPortfolio()
      ]);
      
      if (configResponse && configResponse.success && configResponse.config) {
        setConfig(configResponse.config);
      }
      
      console.log('Portfolio API Response:', projectsResponse);
      
      if (projectsResponse && projectsResponse.data) {
        setProjects(projectsResponse.data);
        console.log('Portfolio loaded:', projectsResponse.data.length, 'projects');
      } else {
        console.warn('No portfolio data received');
        setProjects([]);
        setError('No portfolio data available');
      }
    } catch (error) {
      console.error('Error fetching portfolio:', error);
      setError('Failed to load portfolio. Please try again later.');
      
      // Fallback sample data
      const sampleProjects = getSampleProjects();
      setProjects(sampleProjects);
    } finally {
      setLoading(false);
    }
  };

  const getSampleProjects = () => {
    return [
      {
        id: 1,
        title: 'Green The Future Programme',
        description: 'Developed a comprehensive environmental sustainability platform that enables organizations to track, manage, and report on their green initiatives.',
        client_name: 'Green The Future Programme',
        project_type: 'Web Application & Mobile App',
        technologies: 'React, Node.js, MongoDB, AWS, React Native',
        project_url: 'https://greenthefuture.org',
        featured: true,
        completed_date: '2023-08-15'
      },
      {
        id: 2,
        title: 'Centre for Indigenous Child Rights Platform',
        description: 'Built a comprehensive child rights advocacy and case management system for indigenous communities.',
        client_name: 'Centre for Indigenous Child Rights',
        project_type: 'Web Application',
        technologies: 'Vue.js, Laravel, MySQL, DigitalOcean',
        project_url: 'https://cicr.org',
        featured: true,
        completed_date: '2023-06-20'
      },
      {
        id: 3,
        title: 'Impact Generation Africa Platform',
        description: 'Created a digital transformation platform for social impact measurement and stakeholder engagement.',
        client_name: 'Impact Generation Africa',
        project_type: 'Web Application',
        technologies: 'React, Python Django, PostgreSQL, Docker',
        project_url: 'https://impactgeneration.africa',
        featured: true,
        completed_date: '2023-10-05'
      },
      {
        id: 4,
        title: 'FindLawyer Kenya Platform',
        description: 'Developed Kenya\'s premier legal services marketplace connecting clients with qualified lawyers.',
        client_name: 'FindLawyer Kenya',
        project_type: 'Web & Mobile Application',
        technologies: 'React, React Native, Python Flask, MySQL, M-Pesa API',
        project_url: 'https://findlawyer.co.ke',
        featured: true,
        completed_date: '2023-07-18'
      }
    ];
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress />
        <Typography variant="body1" color="text.secondary">
          Loading portfolio...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', py: 8 }}>
      <Container maxWidth="lg">
        <motion.div 
          initial={{ opacity: 0, y: 50 }} 
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Typography 
            variant="h2" 
            textAlign="center" 
            gutterBottom 
            sx={{ color: theme.palette.primary.main, mb: 2 }}
          >
            {config?.hero_title || 'Work we have shipped'}
          </Typography>
          <Typography 
            variant="h5" 
            textAlign="center" 
            sx={{ mb: 6, color: 'text.secondary', maxWidth: 640, mx: 'auto', lineHeight: 1.6 }}
          >
            {config?.hero_subtitle || 'NGOs, health, legal, labs, impact platforms — real systems in production. Want something like this? Tell us what you need; we will scope it.'}
          </Typography>
        </motion.div>

        {error && (
          <Alert severity="warning" sx={{ mb: 4 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={4}>
          {projects.length > 0 ? (
            projects.map((project, index) => (
              <Grid item xs={12} md={6} key={project.id || index}>
                <motion.div 
                  initial={{ opacity: 0, y: 30 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card 
                    sx={{ 
                      height: '100%', 
                      p: 3,
                      transition: 'all 0.3s ease-in-out',
                      border: project.featured ? `2px solid ${theme.palette.secondary.main}` : 'none',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                      }
                    }}
                  >
                    <CardContent>
                      {project.featured && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            backgroundColor: theme.palette.secondary.main,
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            fontWeight: 'bold'
                          }}
                        >
                          Featured
                        </Box>
                      )}
                      
                      <Typography 
                        variant="h5" 
                        gutterBottom 
                        sx={{ 
                          color: 'primary.main',
                          fontWeight: 600,
                          pr: project.featured ? 4 : 0
                        }}
                      >
                        {project.title}
                      </Typography>
                      
                      <Typography 
                        variant="body1" 
                        paragraph 
                        sx={{ 
                          color: 'text.secondary',
                          lineHeight: 1.6,
                          minHeight: '80px'
                        }}
                      >
                        {project.description}
                      </Typography>
                      
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Client:</strong> {project.client_name}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Project Type:</strong> {project.project_type}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Technologies:</strong> {project.technologies}
                        </Typography>
                        {project.completed_date && (
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Completed:</strong> {new Date(project.completed_date).toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'long' 
                            })}
                          </Typography>
                        )}
                      </Box>
                      
                      {project.project_url && (
                        <Button 
                          variant="outlined" 
                          color="secondary"
                          href={project.project_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ mr: 2 }}
                        >
                          Live Demo
                        </Button>
                      )}
                      
                      <Button variant="outlined" color="primary" component={Link} to="/request-quote">
                        Build something similar
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Box 
                textAlign="center" 
                sx={{ 
                  py: 8,
                  backgroundColor: 'background.paper',
                  borderRadius: 2,
                  border: `1px dashed ${theme.palette.divider}`
                }}
              >
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No portfolio projects found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Our portfolio projects will appear here once they are added.
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </Container>
      <PageSalesCta />
    </Box>
  );
};

export default Portfolio;